import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react"

// ** Third Party Components
import axios from "axios"
import { format } from "date-fns"
import { parseCookies } from "nookies"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"
import { DollarSign, Calculator, X, FileText, Upload } from "lucide-react"

// ** Theme Components
import { Label } from "@components/common/label"
import { Badge } from "@components/common/badge"
import { Input } from "@components/common/input"
import { Button } from "@components/common/button"
import { Card, CardContent, CardHeader, CardTitle } from "@components/common/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog"

// ** Types
import { DictType } from "@pma/types"

// ** Utils
import { cn, getFinancialErrorMessage } from "@pma/utils/helpers"
import { formatCurrency } from "@pma/utils/utils"
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** API Routes
import {
  FINANCIAL_SERVER_URL,
  LAST_SIGNATURE_ENDPOINT,
  SIGN_BILL_ENDPOINT,
} from "@pma/utils/api-routes"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import BoqTablePreview from "./boq-table-preview"
interface BulkSignDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedBills: Array<{ id: string; name: string; amount: string; project: string }>
  setSelectedItems: Dispatch<SetStateAction<Array<any>>>
  onSign: CallableFunction
  bill: DictType
  finishSign?: boolean
}

export function BulkSignDialog({
  open,
  onOpenChange,
  selectedBills,
  onSign,
  bill,
  finishSign,
  setSelectedItems,
}: BulkSignDialogProps) {
  const navigate = useNavigate()
  const [searchParam] = useSearchParams()
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const newParams = new URLSearchParams(searchParam)
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const fileInputRef = useRef<HTMLInputElement>(null)

  const [retention, setRetention] = useState(bill?.retention_percent?.toString() || "")
  const [isDragging, setIsDragging] = useState(false)
  const [signature, setSignature] = useState<File[]>([])
  const [useLastSignature, setUseLastSignature] = useState(false)
  const [expandedSignature, setExpandedSignature] = useState(false)
  const [adjustments, setAdjustments] = useState<Record<string, string>>({})

  const allBillItems = bill?._bill_items
  const adjustmentBillItems =
    allBillItems?.filter((item: DictType) =>
      selectedBills.some((bill) => bill == item.boq_item_id),
    ) || []

  useEffect(() => {
    if (bill) setRetention(bill?.retention_percent)
  }, [bill])

  const handleAmountChange = (billId: string, newAmount: string) => {
    setAdjustments((prev) => ({ ...prev, [billId]: newAmount }))
  }

  const handleSign = () => {
    const adjustmentData = adjustmentBillItems.map((bill: DictType) => ({
      bill_item_id: bill?.id,
      amount: adjustments[bill.id] || bill?.approved_amount,
      declined: false,
    }))

    handleBillSignMutation.mutate(adjustmentData)
  }

  const getLastSignature = async () => {
    const response = await axios.get(FINANCIAL_SERVER_URL + LAST_SIGNATURE_ENDPOINT, {
      headers: {
        Authorization: "Bearer " + accessToken,
        "Content-Type": "application/json",
      },
    })
    return response.data
  }

  const signBill = async (billItems: DictType[]) => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + SIGN_BILL_ENDPOINT,
      {
        bill_id: bill?.id,
        items: billItems,
        sign: signature[0],
        use_last_signature: useLastSignature,
        finish_signing: finishSign,
        ...(finishSign && retention ? { retention: parseFloat(retention) } : {}),
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "multipart/form-data",
        },
      },
    )
    return response.data
  }

  const { data, refetch, isFetched } = useQuery({
    queryKey: ["last-signature"],
    queryFn: getLastSignature,
  })

  const handleBillSignMutation = useMutation({
    mutationKey: ["sign-bill-items"],
    mutationFn: signBill,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("bill items signed successfully"),
        variant: "success",
      })
      setSelectedItems([])

      onSign()
      newParams.set("revalidate", "bills")
      newParams.set("bill_tab", "signed")

      navigate(`?${newParams.toString()}`)
      resetForm()
      onOpenChange(false)
    },
    onError: (error: any) => {
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "sign bill", translate),
        variant: "destructive",
      })
    },
  })

  const resetForm = () => {
    setAdjustments({})
    setSignature([])
    setRetention("")
  }

  const handleCancel = () => {
    resetForm()
    onOpenChange(false)
  }

  const calculateTotals = () => {
    const originalTotal = adjustmentBillItems?.reduce((sum: any, bill: any) => {
      return (
        sum + (Number.parseFloat(bill?.amount) || 0) * (Number.parseFloat(bill?._boq_item?.discount_price) || 0)
      )
    }, 0)

    const newTotal = adjustmentBillItems?.reduce((sum: any, bill: any) => {
      const newAmount = adjustments[bill.id]
        ? Number.parseFloat(adjustments[bill.id]) &&
          Number.parseFloat(adjustments[bill.id]) *
            Number.parseFloat(bill?._boq_item?.discount_price)
        : Number.parseFloat(bill?.amount) * Number.parseFloat(bill?._boq_item?.discount_price)
      return sum + (isNaN(newAmount) ? 0 : newAmount)
    }, 0)

    const difference = newTotal - originalTotal
    const percentageChange = originalTotal > 0 ? (difference / originalTotal) * 100 : 0

    return { originalTotal, newTotal, difference, percentageChange }
  }

  const { originalTotal, newTotal, difference, percentageChange } = calculateTotals()

  const hasChanges = Object.keys(adjustments).some((billId) => {
    const originalAmount = Number.parseFloat(
      adjustmentBillItems?.find((b: DictType) => b.id == billId)?.amount || "0",
    )

    const newAmount = Number.parseFloat(adjustments[billId] || "0")
    return !isNaN(newAmount) && newAmount !== originalAmount
  })

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files)
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files)
    }
  }

  const handleFiles = (files: FileList) => {
    const newFiles = Array.from(files).filter(
      (file) => file.type.startsWith("image/") || file.type === "application/pdf",
    )

    if (newFiles.length > 0) {
      setSignature((prev) => [...prev, ...newFiles])
    }
  }

  const removeAttachment = (index: number) => {
    setSignature((prev) => prev.filter((_, i) => i !== index))
  }

  const renderPreview = (file: File, index: number, url?: string) => {
    const isImage = file.type.startsWith("image/")

    return (
      <div key={`${file.name}-${index}`} className='relative w-fit'>
        <div className='relative overflow-hidden rounded-md border bg-muted/30 transition-opacity group-hover:opacity-80'>
          {isImage || url ? (
            <div className='relative h-44 w-fit overflow-hidden'>
              <img
                src={url ? url : URL.createObjectURL(file) || "/placeholder.svg"}
                alt={file.name}
                className='h-full w-full object-contain'
              />
            </div>
          ) : (
            <div className='flex h-24 w-24 items-center justify-center'>
              <FileText className='h-10 w-10 text-muted-foreground' />
            </div>
          )}
        </div>
        <div className='flex w-full justify-between'>
          <p className='mt-1 max-w-[96px] truncate text-xs'>{file.name}</p>
          {!url && (
            <Button
              type='button'
              variant={"link"}
              onClick={() => removeAttachment(index)}
              className='flex items-center justify-center text-destructive'
              aria-label='Remove attachment'
            >
              {translate("Replace")}
              <X className='h-3 w-3' />
            </Button>
          )}
          {expandedSignature && (
            <Button variant={"link"} size={"sm"} onClick={() => setExpandedSignature(false)}>
              {translate("Collapse Signature")}
            </Button>
          )}
          {url && (
            <small>
              {translate("Uploaded on")}: {format(new Date(data?.created_at), "P")}
            </small>
          )}
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (isFetched && data?.id) setUseLastSignature(true)
  }, [bill, isFetched])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex h-full max-h-[95vh] w-full max-w-[95vw] sm:max-w-[95vw] flex-col overflow-y-auto",
          isHebrew ? "rtl" : "ltr",
        )}
      >
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>{translate("Sign Bill")}</DialogTitle>
          <DialogDescription className={cn(isHebrew ? "text-right" : "text-left")}>
            {translate("Sign amounts for selected items")}. ({selectedBills.length})
          </DialogDescription>
        </DialogHeader>
        <div className='grid gap-6 py-4'>
          <Card>
            <CardHeader className='pb-3'>
              <CardTitle className='text-base'>{translate("Sign Summary")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='mb-4 grid grid-cols-2 gap-4 text-sm'>
                <div>
                  <span className='text-muted-foreground'>{translate("Approved Total")}:</span>
                  <p className='text-lg font-medium'>{formatCurrency(originalTotal, isHebrew)}</p>
                </div>

                <div>
                  <span className='text-muted-foreground'>
                    {finishSign ? translate("Bill total") : translate("Signed Total")}:
                  </span>
                  <p className='text-lg font-medium'>{formatCurrency(newTotal, isHebrew)}</p>
                </div>

                <div className='col-span-2 flex items-center justify-start gap-2 text-sm'>
                  <span className='text-muted-foreground'>{translate("Total Items")}:</span>
                  <Badge variant='secondary'>
                    {adjustmentBillItems?.length} {translate("items")}
                  </Badge>
                </div>
                {hasChanges && (
                  <div className='col-span-2'>
                    <div
                      className={`flex items-center gap-2 ${difference >= 0 ? "text-green-600" : "text-red-600"}`}
                    >
                      <Calculator className='h-4 w-4' />
                      <span>
                        {difference >= 0
                          ? translate("Total Increase")
                          : translate("Total Decrease")}
                        :{formatCurrency(Math.abs(difference), isHebrew)} (
                        {Math.abs(percentageChange).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {useLastSignature ? (
                <>
                  {expandedSignature ? (
                    <>{renderPreview(data?.signature as File, 0, data?.signature?.url)}</>
                  ) : (
                    <>
                      <Button
                        variant={"link"}
                        size={"sm"}
                        onClick={() => setExpandedSignature(true)}
                      >
                        {translate("Expand Signature")}
                      </Button>
                      {data?.signature && (
                        <Button
                          variant={"link"}
                          size={"sm"}
                          onClick={() => setUseLastSignature(false)}
                        >
                          {translate("Update Signature")}
                        </Button>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div className='grid gap-6 pb-4'>
                  <div className='grid gap-2'>
                    <Label>{translate("Signature")}</Label>
                    {signature.length < 1 ? (
                      <div>
                        <div
                          className={cn(
                            "cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors",
                            isDragging
                              ? "border-primary bg-primary/5"
                              : "border-muted-foreground/25 hover:border-primary/50",
                          )}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <input
                            type='file'
                            ref={fileInputRef}
                            onChange={handleFileInputChange}
                            accept='image/*,application/pdf'
                            className='hidden'
                            multiple
                          />
                          <div className='flex flex-col items-center gap-2'>
                            <div className='flex h-10 w-10 items-center justify-center rounded-full bg-primary/10'>
                              <Upload className='h-5 w-5 text-primary' />
                            </div>
                            <div className='flex flex-col space-y-1'>
                              <p className='text-sm font-medium'>
                                {isDragging
                                  ? translate("Drop files here")
                                  : translate("Upload signature")}
                              </p>
                              <p className='text-xs text-muted-foreground'>
                                {translate("Drag and drop image or click to browse")}
                              </p>
                            </div>
                          </div>
                        </div>

                        {data?.signature && (
                          <Button
                            variant={"link"}
                            size={"sm"}
                            onClick={() => {
                              setUseLastSignature(true)
                              setExpandedSignature(true)
                            }}
                          >
                            {translate("Use Saved Signature")}
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className='mt-4'>
                        <Label className='mb-2 block'>{translate("Uploaded File")}</Label>
                        <div className='flex gap-4'>
                          {signature?.map((file, index) => renderPreview(file, index))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {finishSign && (
            <div className='grid gap-2'>
              <Label htmlFor='retention-sign'>{translate("Retention")} (%)</Label>
              <Input
                id='retention-sign'
                type='number'
                min='0'
                max='100'
                step='0.01'
                inputMode='numeric'
                placeholder={translate("Enter retention percentage")}
                value={retention}
                onChange={(e) => {
                  const value = e.target.value
                  if (value === "" || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                    setRetention(value)
                  }
                }}
                className='max-w-xs'
              />
              <p className='text-xs text-muted-foreground'>
                {translate("Enter retention percentage value")} (0-100%)
              </p>
            </div>
          )}

          <div className='grid gap-2'>
            <Label>{translate("Bill Amount Sign")}</Label>
            <p className='text-sm'>
              {translate(
                "You can adjust the amounts before signing by updating the signed amount section",
              )}
              .
            </p>
            <div className='rounded-lg border'>
              <BoqTablePreview
                title='Signed Amount'
                data={adjustmentBillItems}
                handleAmountChange={handleAmountChange}
                adjustments={adjustments}
                isApprove={true}
              />
            </div>
          </div>
        </div>
        <DialogFooter className={cn(isHebrew ? "!space-x-reverse" : "")}>
          <Button variant='outline' onClick={handleCancel}>
            {translate("Cancel")}
          </Button>
          <Button
            onClick={handleSign}
            loading={handleBillSignMutation.isPending}
            disabled={handleBillSignMutation.isPending}
          >
            <DollarSign className='mr-1 h-4 w-4' />
            {!finishSign ? translate("Sign Selected") : translate("Sign Bill")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
